import "./index.css";
import { IoCheckmarkCircle, IoCloseCircle } from "react-icons/io5";
import moment from "moment";
import { FaClock } from "react-icons/fa6";
import { Dialog, DialogTrigger } from "components/Dialog";
import RefundModal from "../Modals/Actions/Refund";
import RetryModal from "../Modals/Actions/Retry";
import {
  StudentToOrgTransactionResponse,
  StudentLimitedResponse,
  MeetingAdminResponse,
  AccountingSystemForCalculatingFinancialStatistics,
  ApprovalStatus,
  StudentToOrgTransactionType,
  Auth0AccountRole,
  TransactionStatus,
  PaymentMethodType,
} from "client/openapi";
import { Button, ButtonColor, ButtonFill, ButtonSize } from "components/Button";
import MeetingDialog from "components/MeetingDialog";
import TransactionDetailsPopup from "./TransactionMeetingDetails";
import {
  getTransactionStatusColor,
  isPaymentSuccessful,
  isFailed,
} from "./statusHelper";
import { useState, useRef, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";

function ServiceTooltip({ service }: { service: string | null | undefined }) {
  const [show, setShow] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const ref = useRef<HTMLDivElement>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHideTimeout = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
  }, []);

  const handleEnter = useCallback(() => {
    clearHideTimeout();
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 4, left: rect.left });
    }
    setShow(true);
  }, [clearHideTimeout]);

  const handleLeave = useCallback(() => {
    hideTimeout.current = setTimeout(() => setShow(false), 150);
  }, []);

  useEffect(() => {
    if (!show) return;
    const hide = () => setShow(false);
    window.addEventListener("scroll", hide, true);
    return () => window.removeEventListener("scroll", hide, true);
  }, [show]);

  const hasNewlines = service && service.includes("\n");

  return (
    <div ref={ref} onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <div className="line-clamp-2">{service}</div>
      {show &&
        hasNewlines &&
        createPortal(
          <div
            style={{ top: pos.top, left: pos.left }}
            className="fixed z-50 p-3 bg-white border border-gray-300 rounded-md shadow-lg whitespace-pre-line text-sm w-max max-w-[600px]"
            onMouseEnter={clearHideTimeout}
            onMouseLeave={handleLeave}
          >
            {service}
          </div>,
          document.body
        )}
    </div>
  );
}

/**
 * Map Student->Org transaction types to human-readable labels
 */
export const studentToOrgTransactionTypeToHumanReadable = [
  { value: StudentToOrgTransactionType.DIRECT, label: "Direct charge" },
  {
    value: StudentToOrgTransactionType.CHARGE_FOR_CREDITS,
    label: "Charge for Credits",
  },
  {
    value: StudentToOrgTransactionType.AUTOMATIC_CHARGE_FOR_A_GROUP_OF_MEETINGS,
    label: "Automatic Charge",
  },
  {
    value: StudentToOrgTransactionType.MEMBER_OF_AUTOMATIC_CHARGE_GROUP,
    label: "Member of Automatic Charge Group",
  },
  {
    value: StudentToOrgTransactionType.MEMBER_OF_AUTOMATIC_PRECHARGE_GROUP,
    label: "Pre-Billed Meeting",
  },
  {
    value: StudentToOrgTransactionType.MEETING_PAID_WITH_CREDITS,
    label: "Meeting Paid with Credits",
  },
  {
    value: StudentToOrgTransactionType.CREDITS_ADDED_MANUALLY,
    label: "Credits Added Manually",
  },
  {
    value: StudentToOrgTransactionType.CREDITS_DEDUCTED_MANUALLY,
    label: "Credits Deducted Manually",
  },
  {
    value: StudentToOrgTransactionType.CANCELLATION_FEE,
    label: "Cancellation Fee",
  },
  {
    value: StudentToOrgTransactionType.RESCHEDULING_FEE,
    label: "Rescheduling Fee",
  },
  { value: StudentToOrgTransactionType.ABSENCE_FEE, label: "Absence Fee" },
  { value: StudentToOrgTransactionType.REFUND, label: "Refund" },
];

export const paymentMethodToHumanReadable = [
  { value: PaymentMethodType.ACH, label: " - ACH" },
  { value: PaymentMethodType.CARD, label: " - Card" },
  { value: PaymentMethodType.INVOICE, label: " - Invoice" },
  {
    value: PaymentMethodType.EXTERNAL_PAYMENT_METHOD,
    label: " - External Payment Method",
  },
  { value: PaymentMethodType.TADPOLE_CREDITS, label: "" },
];

/**
 * Decide if this status implies a "Refund" or "Retry" action.
 */
const getStatusAction = (status: TransactionStatus) => {
  if (isPaymentSuccessful(status)) {
    return { value: "SUCCESSFUL", label: "Refund" };
  }
  if (isFailed(status)) {
    return { value: "FAILED", label: "Retry" };
  }
  return null;
};

function Transactions({
  transactions,
  student,
  accountingSystem,
}: {
  transactions: StudentToOrgTransactionResponse[] | MeetingAdminResponse[];
  student: StudentLimitedResponse;
  accountingSystem: AccountingSystemForCalculatingFinancialStatistics;
}) {
  const isMeetingSystem =
    accountingSystem ===
    AccountingSystemForCalculatingFinancialStatistics.MEETINGS;

  const isTransactions =
    accountingSystem ===
    AccountingSystemForCalculatingFinancialStatistics.TRANSACTIONS;

  return (
    <div className="w-full max-h-[50rem] overflow-y-auto sm:overflow-x-visible max-sm:overflow-x-auto">
      <table className="w-full border-collapse">
        {/* Sticky header row (on vertical scroll) */}
        <thead className="sticky top-0 z-3 shadow-sm bg-white">
          {isTransactions && (
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-2 text-sm font-bold w-[22.5%]">
                Date
              </th>
              <th className="text-left p-2 text-sm font-bold w-[15%]">
                Amount
              </th>
              <th className="text-left p-2 text-sm font-bold w-[20%]">
                Service
              </th>
              <th className="text-left p-2 text-sm font-bold w-[10%]">Type</th>
              <th className="p-2 text-sm font-bold w-[10%] text-center">
                Status
              </th>
              <th className="p-2 text-sm font-bold w-[12.5%] text-center">
                Actions
              </th>
            </tr>
          )}
          {isMeetingSystem && (
            <tr className="border-b-2 border-gray-300">
              <th className="text-left p-2 text-sm font-bold w-[8.5%]">Date</th>
              <th className="text-left p-2 text-sm font-bold w-[18%]">
                Service
              </th>
              <th className="text-left p-2 text-sm font-bold w-[19%]">
                Revenue
              </th>
              <th className="text-left p-2 text-sm font-bold w-[20%]">
                Compensation
              </th>
              <th className="text-left p-2 text-sm font-bold w-[10%]">
                Net Income
              </th>
              <th className="p-2 text-sm font-bold w-[10%] text-center">
                Status
              </th>
              <th className="p-2 text-sm font-bold w-[10%] text-center">
                Actions
              </th>
            </tr>
          )}
        </thead>

        <tbody>
          {transactions.map((transaction, index) => {
            if (isTransactions) {
              const txn = transaction as StudentToOrgTransactionResponse;
              const date = moment(txn.timestamp).format("MM/DD/YYYY");
              const amount = (txn.amount / 100).toFixed(2);
              const service = txn.reason;
              const type =
                (studentToOrgTransactionTypeToHumanReadable.find(
                  (t) => t.value === txn.student_to_org_transaction_type
                )?.label ?? "Unknown") +
                (txn.payment_method_type ===
                PaymentMethodType.EXTERNAL_PAYMENT_METHOD
                  ? " - " + txn.external_payment_method_source
                  : paymentMethodToHumanReadable.find(
                      (t) => t.value === txn.payment_method_type
                    )?.label ?? "");

              const statusIcon = (() => {
                const statusColor = getTransactionStatusColor(txn.status);
                switch (statusColor) {
                  case "green":
                    return <IoCheckmarkCircle className="mx-auto green-mark" />;
                  case "purple":
                    return (
                      <IoCheckmarkCircle className="mx-auto purple-mark" />
                    );
                  case "orange":
                    return (
                      <FaClock
                        className="mx-auto"
                        style={{ color: "#FFB341", fontSize: "20px" }}
                      />
                    );
                  case "red":
                    return <IoCloseCircle className="mx-auto red-mark" />;
                  default:
                    return (
                      <FaClock
                        className="mx-auto"
                        style={{ color: "lightblue", fontSize: "20px" }}
                      />
                    );
                }
              })();

              const statusAction = getStatusAction(txn.status);

              const actions = (
                <div className="flex flex-row gap-1 items-center justify-center">
                  {[
                    StudentToOrgTransactionType.MEETING_PAID_WITH_CREDITS,
                    StudentToOrgTransactionType.AUTOMATIC_CHARGE_FOR_A_GROUP_OF_MEETINGS,
                    StudentToOrgTransactionType.CHARGE_FOR_CREDITS,
                    StudentToOrgTransactionType.MEMBER_OF_AUTOMATIC_CHARGE_GROUP,
                    StudentToOrgTransactionType.MEMBER_OF_AUTOMATIC_PRECHARGE_GROUP,
                    StudentToOrgTransactionType.REFUND,
                  ].includes(txn.student_to_org_transaction_type) && (
                    <LazyTransactionDetailsPopup transaction={txn} />
                  )}

                  {statusAction?.label === "Refund" &&
                    !txn.refunded &&
                    txn.student_to_org_transaction_type !==
                      StudentToOrgTransactionType.CREDITS_ADDED_MANUALLY &&
                    txn.student_to_org_transaction_type !==
                      StudentToOrgTransactionType.CREDITS_DEDUCTED_MANUALLY && (
                      <LazyRefundModal transaction={txn} student={student} />
                    )}

                  {statusAction?.label === "Retry" &&
                    !txn.retried &&
                    txn.student_to_org_transaction_type !==
                      StudentToOrgTransactionType.REFUND && (
                      <LazyRetryModal transaction={txn} student={student} />
                    )}
                </div>
              );

              return (
                <tr
                  key={index}
                  className="bg-white border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">{date}</td>
                  <td className="p-2 text-sm">${amount}</td>
                  <td className="p-2 text-sm">
                    <ServiceTooltip service={service} />
                  </td>
                  <td className="p-2 text-sm">{type}</td>
                  <td className="p-2 text-sm text-center">{statusIcon}</td>
                  <td className="p-2 text-sm text-center">{actions}</td>
                </tr>
              );
            }

            if (isMeetingSystem) {
              const meeting = transaction as MeetingAdminResponse;
              const date = moment(meeting.start).format("MM/DD/YYYY");
              const service = meeting.name;
              const revenue = meeting.students
                .filter((ms) => ms.student.id === student.id)
                .reduce(
                  (sum, ms) =>
                    sum +
                    (ms.rate_student_billed_at / 100) * (meeting.duration / 60),
                  0
                );
              const totalTutorCost = meeting.tutors.reduce(
                (sum, mt) =>
                  sum + (mt.rate_tutor_paid_at / 100) * (meeting.duration / 60),
                0
              );
              const compensation =
                meeting.students.length > 0
                  ? totalTutorCost / meeting.students.length
                  : 0;
              const netIncome = revenue - compensation;

              const statusIcon =
                meeting.reviewed_and_approved_by_admin ===
                ApprovalStatus.APPROVED ? (
                  <IoCheckmarkCircle className="mx-auto green-mark" />
                ) : meeting.reviewed_and_approved_by_admin ===
                  ApprovalStatus.DENIED ? (
                  <IoCloseCircle className="mx-auto red-mark" />
                ) : (
                  <FaClock
                    className="mx-auto"
                    style={{ color: "#FFB341", fontSize: "20px" }}
                  />
                );

              const actions = (
                <div className="flex flex-row gap-1 items-center justify-center">
                  <LazyMeetingDialog meeting={meeting} />
                </div>
              );

              return (
                <tr
                  key={index}
                  className="bg-white border-b border-gray-200 hover:bg-gray-50"
                >
                  <td className="p-2 text-sm">{date}</td>
                  <td className="p-2 text-sm">{service}</td>
                  <td className="p-2 text-sm">${revenue.toFixed(2)}</td>
                  <td className="p-2 text-sm">${compensation.toFixed(2)}</td>
                  <td className="p-2 text-sm">${netIncome.toFixed(2)}</td>
                  <td className="p-2 text-sm text-center">{statusIcon}</td>
                  <td className="p-2 text-sm text-center">{actions}</td>
                </tr>
              );
            }

            return null;
          })}
        </tbody>
      </table>
    </div>
  );
}

function LazyTransactionDetailsPopup({
  transaction,
}: {
  transaction: StudentToOrgTransactionResponse;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          View
        </Button>
      </DialogTrigger>
      {open && <TransactionDetailsPopup transaction={transaction} />}
    </Dialog>
  );
}

function LazyRefundModal({
  transaction,
  student,
}: {
  transaction: StudentToOrgTransactionResponse;
  student: StudentLimitedResponse;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.SKYBLUE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          Refund
        </Button>
      </DialogTrigger>
      {open && <RefundModal transaction={transaction} student={student} />}
    </Dialog>
  );
}

function LazyRetryModal({
  transaction,
  student,
}: {
  transaction: StudentToOrgTransactionResponse;
  student: StudentLimitedResponse;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.SKYBLUE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          Retry
        </Button>
      </DialogTrigger>
      {open && <RetryModal transaction={transaction} student={student} />}
    </Dialog>
  );
}

function LazyMeetingDialog({ meeting }: { meeting: MeetingAdminResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger>
        <Button
          color={ButtonColor.PURPLE}
          size={ButtonSize.EXTRA_SMALL}
          fill={ButtonFill.HOLLOW}
        >
          View
        </Button>
      </DialogTrigger>
      {open && (
        <MeetingDialog
          isCreate={false}
          role={Auth0AccountRole.ORG_ADMIN}
          event={meeting}
          setEvents={() => Promise.resolve()}
        />
      )}
    </Dialog>
  );
}

export default Transactions;
